/**
 * Convert a colour to the **HSL triplet** form the app's design tokens are
 * declared in , `"35 24% 79%"`, not `"#f1dcc9"`.
 *
 * Why this exists as a shared helper: the token contract is easy to get wrong
 * and fails silently. Every consumer wraps these variables , Tailwind emits
 * `hsl(var(--primary))` for `text-primary`, and styles/design-system.css derives
 * `--color-bg-sunken: hsl(var(--muted))`, `--color-text-primary:
 * hsl(var(--foreground))` and friends. Assigning a hex value produces
 * `hsl(#7c2d12)`, which is invalid CSS: the declaration is dropped, the element
 * renders unpainted, and text falls back to inherited (usually black).
 *
 * That is precisely how the MoMo number field and the proof-upload field on
 * /pay ended up black-on-black. The surrounding page looked correctly themed
 * because `background` and `color` are also set directly, so nothing looked
 * broken until a shadcn primitive tried to read a token.
 *
 * Menu themes author their palettes in hex (lib/menu-themes/themes.json), so
 * anything piping a theme palette into shadcn token names must run it through
 * here first.
 *
 * Accepts #rgb / #rrggbb / #rrggbbaa, and passes through values that are
 * already triplet-shaped (or named colours / gradients it cannot parse) so a
 * caller can hand over either form safely.
 */
export function toHslTriplet(input: string): string {
  const value = (input || '').trim();
  // Already a triplet ("210 16% 18%") , leave it alone.
  if (/^-?[\d.]+\s+[\d.]+%\s+[\d.]+%$/.test(value)) return value;

  const hex = value.replace(/^#/, '');
  const full =
    hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex.length === 8 ? hex.slice(0, 6) : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return value; // named colour, gradient, oklch…

  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  const round = (n: number) => Math.round(n * 10) / 10;
  return `${round(h * 360)} ${round(s * 100)}% ${round(l * 100)}%`;
}
